<template>
  <!-- Product table list -->
  <div class="invoice-import-products hiddentable">
    <AddEditProductModal 
      v-if="showProductModal"
      allow-group-creation
      :product="productToEdit"
      @close="closeProductModal"
      @group-created="retrieveProducts"
      @product-created="retrieveProducts"
      @product-updated="retrieveProducts"
    />
    
    <EmptyState v-if="!items || items.length === 0">
      <template v-slot:image>
        <img src="/static/img/empty-states/empty-state-cost.png">
      </template>
      <template v-slot:title>
        {{ $gettext('We couldn\'t find any products') }}
      </template>
      <template v-slot:content>
        {{ $gettext('Start by creating a new one.') }}
      </template>
      <template v-slot:cta>
        <GekkoButton
          :text="$gettext('Add product')"
          icon="fa-solid fa-plus"
          @click="openProductModal(null)"
        />
      </template>
    </EmptyState>

    <div class="selected-items-wrap">
      <div v-for="itemGroup in groupsWithProducts" :key="itemGroup.name" class="product-group-wrap">
        <div class="product-group-name">
          {{ itemGroup.name }}
        </div>
        <div class="product-list-group-table-wrap">
          <b-table
            :items="itemGroup.products"
            :class="['product-list-table gk-table gk-table--responsive gk-table--headed gk-table--striped']"
            :fields="groupTableFields"
            :tbody-tr-class="rowClass"
            @row-clicked="rowClicked"
          >
            <template #cell(description)="data">
              {{ data.item.description }}
            </template>
            <template #cell(quantity)="row">
              <span class="gk-input-light product-quantity-selector">
                <GekkoButton 
                  @click="substractQuantity(row.item)"
                  :icon="'fa-solid fa-minus'"
                  variant="light"
                  class="text-brand"
                />
                <b-form-input v-model="row.item.multiplier" type="number" required />
                <GekkoButton 
                  @click="addQuantity(row.item)"
                  :icon="'fa-solid fa-plus'"
                  variant="light"
                  class="text-brand"
                />
              </span>
            </template>
            <template #cell(amount)="data">
              €{{ data.item.amount | formatNumber }}
            </template>
            <template #cell(tax_rate)="data">
              <div v-if="!data.item.vat_exempt">{{ data.item.tax_rate }}%</div>
              <div v-if="data.item.vat_exempt" v-translate> VAT Exempt </div>
            </template>
            <template #cell(total_inc_vat)="data">
              €{{ data.item.total_inc_vat | formatNumber }}
            </template>
          </b-table>
        </div>
      </div>
    </div>
    <div class="import-actions-bar">
      <div>
        <span class="text-blue">{{ $gettext('Total excluding VAT') }}</span> € {{ totalAmount | formatNumber }}
      </div>
      <div class="import-actions-bar--buttons">
        <div style="margin-right: 1rem; font-size: 14px;">
          {{ $gettext('Import a total of') }} {{ selectedProducts.length }} {{ $gettext('lines') }}
        </div>
        <GekkoButton 
          @click="$emit('close', false)"
          :text="$gettext('Cancel')"
          variant="light"
        />
        <GekkoButton 
          @click="$emit('add-lines', selectedProducts)"
          :text="(isQuotation) ? $gettext('Add to quotation') : $gettext('Add to invoice')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import ProductService from "@/store/services/product.service.js"
import ProductGroupService from "@/store/services/productgroup.service.js"

import FilterBar from "@/components/list_filters/FilterBar"
import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import ImportAggregateTrips from "@/components/invoices/ui/ImportAggregateTrips"

import AddEditProductModal from "@/components/products/AddEditProductModal"
import EmptyState from "@/components/ui/EmptyState"

export default {
  name: "ImportProductsToInvoice",
  components: {
    GekkoButton,
    LoadingSpinner,
    FilterBar,
    ImportAggregateTrips,
    AddEditProductModal,
    EmptyState
  },
  props: {
    filterCookieName: {
      type: String,
      default: ''
    },
    invoice: {
      type: Object,
      default: () => {}
    },
    isQuotation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      itemCount: 0,
      groupTableFields: [
        { key: 'description', label: this.$gettext("Description"), tdClass: 'col-description coldesc' },
        { key: 'quantity', label: this.$gettext("Quantity"), tdClass: 'tablecol-xs' },
        { key: 'amount', label: this.$gettext("Amount ex. vat"), tdClass: 'tablecol-xs text-right', thClass: 'text-right' },
        { key: 'tax_rate', label: this.$gettext("Tax rate"), tdClass: 'tablecol-xs text-right', thClass: 'text-right' },
        { key: 'total_inc_vat', label: this.$gettext("Amount inc. vat"), tdClass: 'tablecol-xs text-right', thClass: 'text-right' },
      ],
      items: [],
      groups: [],
      showProductModal: false,
      productToEdit: null
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      selectedCustomer: "invoices/getInvoiceCreateRecipient",
      selectedProject: "invoices/getInvoiceCreateProject",
      getCompanyId: 'main/getCompanyId'
    }),
    groupsWithProducts () {
      return this.groups.filter(group => Object.prototype.hasOwnProperty.call(group, 'products'))
    },
    selectedProducts () {
      let selected = []
      if (this.items) {
        selected = this.items.filter(item => item.selected)
      }
      return selected
    },
    totalAmount () {
      let total = 0
      this.selectedProducts.forEach(item => {
        total += item.amount * item.multiplier
      })
      return total
    },
    currentProductLines () {
      let current_lines = []
      if (this.isQuotation) {
        current_lines = this.invoice.quotationline_set.filter(obj => Object.prototype.hasOwnProperty.call(obj, 'product'))
      } else {
        current_lines = this.invoice.invoice_lines.filter(obj => Object.prototype.hasOwnProperty.call(obj, 'product'))
      }
      return current_lines
    }
  },
  watch: {
  },
  created () {
    this.retrieveGroupsAndProducts()
  },
  mounted () {
  },
  methods: {
    openProductModal () {
      this.showProductModal = true
    },
    closeProductModal () {
      this.showProductModal = false
      this.productToEdit = null
    },
    rowClicked (item) {
      this.showProductModal = true
      this.productToEdit = item
    },
    addQuantity (product) {
      this.$set(product, 'multiplier', (product.multiplier + 1))
      this.$set(product, 'selected', true)
    },
    substractQuantity (product) {
      if (product.multiplier > 0) {
        this.$set(product, 'multiplier', (product.multiplier - 1))
        if (product.multiplier < 1) {
          this.$set(product, 'selected', false)
        }
      }
    },
    retrieveGroupsAndProducts () {
      // Retrieve groups, then products.
      ProductGroupService.list({ company_profile: this.getCompanyId }).then((response) => {
        this.groups = response.data
        this.groups = this.groups.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
        this.groups.unshift({ id: null, name: this.$gettext('No Group') })
        this.retrieveProducts()
      })
    },
    retrieveProducts () {
      ProductService.listForImport({ company_profile: this.getCompanyId }).then((response) => {
        this.items = response.data.sort((a,b) => (a.group > b.group) ? 1 : ((b.group > a.group) ? -1 : 0))
        this.items.forEach(item => {
          this.$set(item, 'multiplier', 0)
          this.$set(item, 'product', item.id)

          // if there is an existing line for this product, get its multiplier
          const existing_product_line = this.currentProductLines.find(added_item => added_item.product === item.id)
          if (existing_product_line) {
            this.$set(item, 'selected', true)
            this.$set(item, 'multiplier', existing_product_line.multiplier)
          } else {
            this.$set(item, 'multiplier', 0)
          }

        })
        this.groups.forEach(group => {
          const group_products = this.items.filter(item => { return item.group === group.id })
          if (group_products.length > 0) {
            this.$set(group, 'products', group_products)
          }
        })
      })
    },
    rowClass (item) {
      if (!item) {
          return '';
      }
      let item_classes = ''
      if (item && item.selected) {
        item_classes += ' item-selected'
      }
      return item_classes
    },
  }
};
</script>

<style lang="scss" scoped>
  .product-list-group-table-wrap {
    .product-quantity-selector {
      display: flex;
      gap: 0.5rem;
      .btn {
        flex: none;
        height: 38px;
        width: 38px;
        border-radius: 100px;
        padding: 0;
      }
      input { width: 100px; }
    }
  }
  .product-list-table {
    .col-description { font-size: 1rem; }
    .tablecol-sm {
      width: 150px;
    }
  }
  .product-group-wrap {
    margin-bottom: 2rem;
    .product-group-name {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      width: 100%;
      font-size: 18px;
      font-family: DINNextLTPro-Medium, sans-serif;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
    }
  }
.invoice-import-products {
  display: flex;
  flex-direction: column;
  // gap: 1rem;
  
  .import-filters {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding-top: 0.5rem;
  }
  .import-actions-bar {
    display: flex;
    justify-content: space-between;
    // gap: 0.5rem;
    align-items: center;
    padding: 1rem 0;
    &--buttons {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      align-items: center;
    }
  }
  .selected-items-wrap {
    min-height: 350px;
  }
  &.hiddentable {
    // margin-bottom: -1rem;
    .import-actions-bar {
      padding-bottom: 0;
    }
    .available-items-wrap {
        height: 0;
        overflow: hidden;
    }
  }
}

</style>

<style lang="scss">

</style>